import React, { useMemo } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faTriangleExclamation,
  faClock,
  faCheckDouble,
  faRuler
} from '@fortawesome/free-solid-svg-icons';

const safeArr = v => (Array.isArray(v) ? v : []);
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = n => Number(n || 0).toLocaleString();
const DAY = 86400000;

const KpiCard = ({ icon, color, value, label, sub }) => (
  <Card className="h-100">
    <Card.Body className="d-flex align-items-center gap-3 py-3">
      <div
        className={`d-flex align-items-center justify-content-center rounded-circle bg-${color}-subtle flex-shrink-0`}
        style={{ width: 44, height: 44 }}
      >
        <FontAwesomeIcon icon={icon} className={`text-${color}`} />
      </div>
      <div className="min-w-0">
        <div className="fs-6 fw-bolder lh-1">{value}</div>
        <div className="text-700 fs-10 fw-semibold">{label}</div>
        {sub && <div className="text-muted fs-10">{sub}</div>}
      </div>
    </Card.Body>
  </Card>
);

const OrderMonitorKpis = ({ orders = [] }) => {
  const kpis = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTs = today.getTime();

    let totalOrdered = 0;
    let totalFulfilled = 0; // produced + reserved-from-stock
    let overdue = 0;
    let soon = 0;

    for (const o of orders || []) {
      const lines = safeArr(o?.lines);
      const ordered = lines.reduce((s, l) => s + num(l.totalOrderedLength), 0);
      const produced = Math.min(
        ordered,
        lines.reduce((s, l) => s + num(l.producedLength), 0)
      );
      // reserved-from-stock counts as fulfilled (no production needed)
      const stock = lines.reduce((s, l) => s + num(l.stockReservedLength), 0);
      const fulfilled = Math.min(ordered, produced + stock);
      totalOrdered += ordered;
      totalFulfilled += fulfilled;

      const complete = ordered > 0 && fulfilled >= ordered;
      const dueTs = o?.dueDate ? new Date(o.dueDate).getTime() : null;
      if (dueTs != null && !complete) {
        if (dueTs < todayTs) overdue += 1;
        else if (dueTs - todayTs <= 7 * DAY) soon += 1;
      }
    }

    const avgPct =
      totalOrdered > 0 ? Math.round((totalFulfilled / totalOrdered) * 100) : 0;
    const remaining = Math.max(0, totalOrdered - totalFulfilled);

    return {
      count: (orders || []).length,
      overdue,
      soon,
      avgPct,
      remaining,
      totalOrdered,
      totalFulfilled
    };
  }, [orders]);

  return (
    <Row xs={2} md={3} xl={5} className="g-3 mb-1">
      <Col>
        <KpiCard
          icon={faClipboardList}
          color="primary"
          value={fmt(kpis.count)}
          label="Active orders"
        />
      </Col>
      <Col>
        <KpiCard
          icon={faTriangleExclamation}
          color="danger"
          value={fmt(kpis.overdue)}
          label="Overdue"
          sub="past due, not delivered"
        />
      </Col>
      <Col>
        <KpiCard
          icon={faClock}
          color="warning"
          value={fmt(kpis.soon)}
          label="Due ≤ 7 days"
          sub="not yet delivered"
        />
      </Col>
      <Col>
        <KpiCard
          icon={faCheckDouble}
          color="success"
          value={`${kpis.avgPct}%`}
          label="Avg fulfillment"
          sub={`${fmt(kpis.totalFulfilled)} / ${fmt(kpis.totalOrdered)} m`}
        />
      </Col>
      <Col>
        <KpiCard
          icon={faRuler}
          color="info"
          value={`${fmt(kpis.remaining)} m`}
          label="Remaining to produce"
        />
      </Col>
    </Row>
  );
};

export default OrderMonitorKpis;
